import type { DocumentTemplateType } from '@/types/blocks'

export interface DocumentTemplateSectionPreset {
	name: string
	color: string
	filterPlayType: 'run' | 'pass' | null
	// Label names to match (will be resolved to IDs at creation time)
	labelNames?: string[]
}

export interface DocumentTemplate {
	id: DocumentTemplateType
	name: string
	description: string
	icon: 'grid' | 'map-pin' | 'users' | 'settings'
	sections: DocumentTemplateSectionPreset[]
}

// Color palette for sections
export const SECTION_COLORS = {
	red: '#dc2626',
	orange: '#ea580c',
	amber: '#d97706',
	yellow: '#ca8a04',
	lime: '#65a30d',
	green: '#16a34a',
	emerald: '#059669',
	teal: '#0d9488',
	cyan: '#0891b2',
	sky: '#0284c7',
	blue: '#2563eb',
	indigo: '#4f46e5',
	violet: '#7c3aed',
	purple: '#9333ea',
	fuchsia: '#c026d3',
	pink: '#db2777',
	rose: '#e11d48',
	slate: '#475569',
} as const

export const DOCUMENT_TEMPLATES: DocumentTemplate[] = [
	{
		id: 'down_and_distance',
		name: 'Down & Distance',
		description: 'Organize plays by down and distance situations (1st & 10, 2nd & Short, 3rd & Long, etc.)',
		icon: 'grid',
		sections: [
			{
				name: '1st & 10',
				color: SECTION_COLORS.blue,
				filterPlayType: null,
				labelNames: ['1st & 10', '1st Down'],
			},
			{
				name: '2nd & Short',
				color: SECTION_COLORS.green,
				filterPlayType: null,
				labelNames: ['2nd & Short', '2nd & 1-3'],
			},
			{
				name: '2nd & Medium',
				color: SECTION_COLORS.teal,
				filterPlayType: null,
				labelNames: ['2nd & Medium', '2nd & 4-6'],
			},
			{
				name: '2nd & Long',
				color: SECTION_COLORS.cyan,
				filterPlayType: null,
				labelNames: ['2nd & Long', '2nd & 7+'],
			},
			{
				name: '3rd & Short',
				color: SECTION_COLORS.orange,
				filterPlayType: null,
				labelNames: ['3rd & Short', '3rd & 1-3'],
			},
			{
				name: '3rd & Medium',
				color: SECTION_COLORS.amber,
				filterPlayType: null,
				labelNames: ['3rd & Medium', '3rd & 4-7'],
			},
			{
				name: '3rd & Long',
				color: SECTION_COLORS.red,
				filterPlayType: null,
				labelNames: ['3rd & Long', '3rd & 8+'],
			},
		],
	},
	{
		id: 'field_zones',
		name: 'Field Zones',
		description: 'Organize plays by field position (Red Zone, Goal Line, Coming Out, etc.)',
		icon: 'map-pin',
		sections: [
			{
				name: 'Coming Out',
				color: SECTION_COLORS.slate,
				filterPlayType: null,
				labelNames: ['Coming Out', 'Own 1-10'],
			},
			{
				name: 'Open Field',
				color: SECTION_COLORS.blue,
				filterPlayType: null,
				labelNames: ['Open Field', 'Between 20s'],
			},
			{
				name: 'Plus Territory',
				color: SECTION_COLORS.green,
				filterPlayType: null,
				labelNames: ['Plus Territory', 'Opp 40-20'],
			},
			{
				name: 'Red Zone',
				color: SECTION_COLORS.orange,
				filterPlayType: null,
				labelNames: ['Red Zone', 'Opp 20-10'],
			},
			{
				name: 'Goal Line',
				color: SECTION_COLORS.red,
				filterPlayType: null,
				labelNames: ['Goal Line', 'Opp 10-GL'],
			},
		],
	},
	{
		id: 'personnel',
		name: 'Personnel Groupings',
		description: 'Organize plays by personnel package (10, 11, 12, 21, etc.)',
		icon: 'users',
		sections: [
			{
				name: '10 Personnel',
				color: SECTION_COLORS.purple,
				filterPlayType: null,
				labelNames: ['10 Personnel', '1 RB 0 TE'],
			},
			{
				name: '11 Personnel',
				color: SECTION_COLORS.blue,
				filterPlayType: null,
				labelNames: ['11 Personnel', '1 RB 1 TE'],
			},
			{
				name: '12 Personnel',
				color: SECTION_COLORS.green,
				filterPlayType: null,
				labelNames: ['12 Personnel', '1 RB 2 TE'],
			},
			{
				name: '21 Personnel',
				color: SECTION_COLORS.orange,
				filterPlayType: null,
				labelNames: ['21 Personnel', '2 RB 1 TE'],
			},
			{
				name: '22 Personnel',
				color: SECTION_COLORS.red,
				filterPlayType: null,
				labelNames: ['22 Personnel', '2 RB 2 TE'],
			},
			{
				name: '13 Personnel',
				color: SECTION_COLORS.teal,
				filterPlayType: null,
				labelNames: ['13 Personnel', '1 RB 3 TE'],
			},
		],
	},
	{
		id: 'custom',
		name: 'Custom',
		description: 'Start with a blank slate and create your own sections',
		icon: 'settings',
		sections: [],
	},
]

export function getTemplateById(id: DocumentTemplateType): DocumentTemplate | undefined {
	return DOCUMENT_TEMPLATES.find(t => t.id === id)
}

export function getDefaultTemplate(): DocumentTemplate {

	return DOCUMENT_TEMPLATES[0]!
}
